import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SuiBox from "components/SuiBox";
import Box from "@mui/material/Box";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import ProcesosApi from "api/procesosApi";
import DatosBasicosApi from "api/datosbasicosApi";
import DeudaCard from "./components/DeudaCard";
import EditDeudaModal from "./components/EditDeudaModal";
import NuevaDeudaForm from "./components/NuevaDeudaForm";

const ORDEN_CLASES = [
  "PRIMERA CLASE",
  "SEGUNDA CLASE",
  "TERCERA CLASE",
  "CUARTA CLASE",
  "QUINTA CLASE",
];

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const DeudasManager = ({ procesoId, showNewDeudaModal, setShowNewDeudaModal }) => {
  const [deudas, setDeudas] = useState([]);
  const [clasesDeuda, setClasesDeuda] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDeuda, setSelectedDeuda] = useState(null);
  const [loading, setLoading] = useState(true);
  //const [totalesPorClase, setTotalesPorClase] = useState({});
  //const [totalAcreencias, setTotalAcreencias] = useState(0);

  const fetchDeudas = async () => {
    try {
      setLoading(true);
      const response = await ProcesosApi.obtenerDeudasProceso(procesoId);
      const deudasArray = response.data?.deudas || [];
      setDeudas(deudasArray.sort((a, b) => a.orden - b.orden));
    } catch (error) {
      console.error("Error al cargar deudas:", error);
      setDeudas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasesDeuda = async () => {
    try {
      const response = await DatosBasicosApi.fetchClasesDeuda();
      setClasesDeuda(response.data.sort((a, b) => a.id - b.id));
    } catch (error) {
      console.error("Error al cargar clases de deuda:", error);
      setClasesDeuda([]);
    }
  };

  useEffect(() => {
    if (procesoId) {
      fetchDeudas();
      fetchClasesDeuda();
    }
  }, [procesoId]);

  const calcularTotales = () => {
    let totalAcreencias = 0;
    const totalesPorClase = {};
    deudas.forEach((deuda) => {
      const clase = deuda.str_clase_deuda?.nombre || "SIN CLASE";
      const capital = Number(deuda.capital) || 0; // Convertir a número
      if (!totalesPorClase[clase]) {
        totalesPorClase[clase] = 0;
      }
      totalesPorClase[clase] += capital;
      totalAcreencias += capital;
    });
    return { totalesPorClase, totalAcreencias };
  };
  // Llamar a la función
  const { totalesPorClase, totalAcreencias } = calcularTotales();
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const sourceClase = result.source.droppableId;
    const destClase = result.destination.droppableId;
    if (sourceClase !== destClase) return;
    // Obtener las deudas de la clase actual
    const deudasClase = deudas.filter((d) => d.str_clase_deuda.nombre === sourceClase);
    const deudasOtrasClases = deudas.filter((d) => d.str_clase_deuda.nombre !== sourceClase);
    // Reordenar las deudas de la clase actual
    const reorderedDeudas = reorder(deudasClase, result.source.index, result.destination.index);
    // Actualizar órdenes
    const deudasActualizadas = reorderedDeudas.map((deuda, index) => ({
      ...deuda,
      orden: index,
    }));
    // Combinar con las deudas de otras clases y actualizar estado
    const newDeudas = [...deudasOtrasClases, ...deudasActualizadas].sort((a, b) => {
      if (a.str_clase_deuda.nombre === b.str_clase_deuda.nombre) {
        return a.orden - b.orden;
      }
      return (
        ORDEN_CLASES.indexOf(a.str_clase_deuda.nombre) -
        ORDEN_CLASES.indexOf(b.str_clase_deuda.nombre)
      );
    });
    // Actualizar estado inmediatamente para UI fluida
    setDeudas(newDeudas);
    // Actualizar en el backend
    try {
      await Promise.all(
        deudasActualizadas.map((deuda) => {
          const datosActualizacion = {
            proceso: procesoId,
            acreedor: deuda.str_acreedor.id,
            clase_deuda: deuda.clase_deuda,
            orden: deuda.orden,
            capital: deuda.capital,
            reconoce_intereses: deuda.reconoce_intereses,
            suma_intereses: deuda.suma_intereses,
            intereses: deuda.intereses,
            naturaleza: deuda.naturaleza,
            dias_mora: deuda.dias_mora,
            rango_mora: deuda.rango_mora,
            tipo_garantia: deuda.tipo_garantia,
            fecha_otorgamiento: deuda.fecha_otorgamiento,
            fecha_vencimiento: deuda.fecha_vencimiento,
            garantia: deuda.garantia,
            apoderado: deuda.apoderado,
            postergado: deuda.postergado,
            costas: deuda.costas,
            pagador_libranza: deuda.pagador_libranza, // Nuevo campo
          };
          return ProcesosApi.actualizarDeuda(procesoId, deuda.id, datosActualizacion);
        })
      );
    } catch (error) {
      console.error("Error al actualizar orden:", error);
      await fetchDeudas(); // Solo recargar en caso de error
    }
  };

  const handleDelete = async (deuda) => {
    if (window.confirm("¿Está seguro(a) de eliminar esta deuda?")) {
      try {
        await ProcesosApi.eliminarDeuda(procesoId, deuda.id);
        await fetchDeudas();
      } catch (error) {
        console.error("Error al eliminar deuda:", error);
      }
    }
  };

  const handleEdit = (deuda) => {
    setSelectedDeuda(deuda);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (deudaActualizada) => {
    //alert("deudaActualizada: " + JSON.stringify(deudaActualizada));
    try {
      const datosCompletos = {
        proceso: procesoId,
        acreedor: deudaActualizada.str_acreedor?.id || deudaActualizada.acreedor,
        clase_deuda: deudaActualizada.clase_deuda,
        orden: deudaActualizada.orden,
        capital: deudaActualizada.capital,
        reconoce_intereses: deudaActualizada.reconoce_intereses,
        suma_intereses: deudaActualizada.suma_intereses,
        intereses: deudaActualizada.intereses,
        naturaleza: deudaActualizada.naturaleza,
        dias_mora: deudaActualizada.dias_mora,
        rango_mora: deudaActualizada.rango_mora,
        tipo_garantia: deudaActualizada.tipo_garantia,
        fecha_otorgamiento: deudaActualizada.fecha_otorgamiento,
        fecha_vencimiento: deudaActualizada.fecha_vencimiento,
        garantia: deudaActualizada.garantia,
        codeudor_avalista: deudaActualizada.codeudor_avalista,
        apoderado: deudaActualizada.apoderado,
        postergado: deudaActualizada.postergado,
        costas: deudaActualizada.costas,
        pagador_libranza: deudaActualizada.pagador_libranza, // Nuevo campo
      };
      await ProcesosApi.actualizarDeuda(procesoId, deudaActualizada.id, datosCompletos);
      // Recarga todas las Deudas
      await fetchDeudas();
      setEditModalOpen(false);
      setSelectedDeuda(null);
    } catch (error) {
      console.error("Error al actualizar deuda:", error);
    }
  };

  const handleNewDeudaSubmit = async (deudaData) => {
    try {
      // Obtener las deudas existentes de la misma clase
      const deudasMismaClase = deudas.filter(
        (d) => d.clase_deuda === Number(deudaData.clase_deuda)
      );
      // Calcular el siguiente orden
      const ultimoOrden =
        deudasMismaClase.length > 0 ? Math.max(...deudasMismaClase.map((d) => d.orden ?? 0)) : -1;
      // Añadir el orden a los datos de la deuda
      const deudaConOrden = {
        ...deudaData,
        orden: ultimoOrden + 1,
      };
      await ProcesosApi.crearDeudas(procesoId, deudaConOrden);
      await fetchDeudas();
      setShowNewDeudaModal(false);
    } catch (error) {
      console.error("Error al crear deuda:", error);
    }
  };

  if (loading) {
    return (
      <SuiBox textAlign="center" py={5}>
        <SuiTypography variant="button" fontWeight="regular">
          Cargando las Acreencias del Deudor...
        </SuiTypography>
      </SuiBox>
    );
  }

  const deudasPorClase = deudas.reduce((acc, deuda) => {
    if (!deuda.str_clase_deuda) {
      // Si no existe str_clase_deuda, intentar encontrarlo en clasesDeuda
      const claseDeuda = clasesDeuda.find((c) => c.id === deuda.clase_deuda);
      if (claseDeuda) {
        const clase = claseDeuda.nombre;
        if (!acc[clase]) {
          acc[clase] = [];
        }
        acc[clase].push({
          ...deuda,
          str_clase_deuda: {
            id: claseDeuda.id,
            nombre: claseDeuda.nombre,
            descripcion: claseDeuda.descripcion,
          },
        });
      }
    } else {
      const clase = deuda.str_clase_deuda.nombre;
      if (!acc[clase]) {
        acc[clase] = [];
      }
      acc[clase].push(deuda);
    }
    return acc;
  }, {});

  // Ordenar las deudas dentro de cada clase por orden
  Object.keys(deudasPorClase).forEach((clase) => {
    deudasPorClase[clase].sort((a, b) => a.orden - b.orden);
  });

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <SuiBox p={2}>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SuiTypography variant="h5" fontWeight="bold">
            Acreencias Registradas - $ {totalAcreencias.toLocaleString()}
          </SuiTypography>
          <SuiButton
            variant="gradient"
            buttonColor="info"
            onClick={() => setShowNewDeudaModal(true)}
          >
            Agregar Acreencia
          </SuiButton>
        </SuiBox>

        {deudas.length === 0 ? (
          <SuiBox textAlign="center" py={5}>
            <SuiTypography variant="button" color="text" fontWeight="regular">
              No hay acreencias registradas. Haga clic en &quot;Agregar Acreencia&quot; para
              comenzar.
            </SuiTypography>
          </SuiBox>
        ) : (
          ORDEN_CLASES.map((clase) => {
            const deudasClase = deudasPorClase[clase] || [];
            if (deudasClase.length === 0) return null;

            return (
              <Droppable droppableId={clase} direction="horizontal" key={clase}>
                {(provided) => (
                  <SuiBox mb={3}>
                    <SuiTypography variant="button" fontWeight="medium" mb={2}>
                      {clase} - $ {totalesPorClase[clase]?.toLocaleString()}
                    </SuiTypography>
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        display: "flex",
                        gap: 3,
                        overflowX: "auto",
                        pb: 2,
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor: "#555",
                          },
                        },
                      }}
                    >
                      {deudasClase.map((deuda, index) => (
                        <Draggable key={deuda.id} draggableId={String(deuda.id)} index={index}>
                          {(providedDrag, snapshot) => (
                            <Box
                              ref={providedDrag.innerRef}
                              {...providedDrag.draggableProps}
                              {...providedDrag.dragHandleProps}
                              sx={{
                                minWidth: "350px",
                                opacity: snapshot.isDragging ? 0.9 : 1,
                                transform: snapshot.isDragging
                                  ? providedDrag.draggableProps.style.transform
                                  : "translate(0, 0)",
                                transitionProperty: "transform",
                                transitionDuration: "0.001s",
                              }}
                            >
                              <DeudaCard
                                deuda={deuda}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  </SuiBox>
                )}
              </Droppable>
            );
          })
        )}

        {selectedDeuda && (
          <EditDeudaModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedDeuda(null);
            }}
            onSubmit={handleEditSubmit}
            deuda={selectedDeuda}
            clasesDeuda={clasesDeuda}
          />
        )}

        <NuevaDeudaForm
          open={showNewDeudaModal}
          onClose={() => setShowNewDeudaModal(false)}
          onSubmit={handleNewDeudaSubmit}
          procesoId={procesoId}
          clasesDeuda={clasesDeuda}
        />
      </SuiBox>
    </DragDropContext>
  );
};

DeudasManager.propTypes = {
  procesoId: PropTypes.number.isRequired,
  showNewDeudaModal: PropTypes.bool.isRequired,
  setShowNewDeudaModal: PropTypes.func.isRequired,
};

export default DeudasManager;
